import { computed, makeObservable } from 'mobx'
import {
  removeUserAnswer,
  setAnswerObjects,
  submitUserAnswer,
  updateAnswerObjects,
  updateAnswerObjectsOrder,
  upsertUserAnswer,
} from '../../firestore/RoomStateAnswer'
import {
  deleteRoomStateCollaborationData,
  updateRoomStateCollaborationDataOrder,
  upsertRoomStateCollaborationData,
} from '../../firestore/RoomStateCollaborationData'
import { CollaborationDataType } from '../../firestore/RoomStateCollaborationData/types'
import { Question } from '../../models/Question'
import type { SlideQuestion } from '../../models/SlideQuestion'
import type { MeetingCubit } from '../MeetingCubit'
import { GroupQuizSlide } from './slides/GroupQuizSlide'

/**
 * The MeetingQuestion a Question that is associated with a Meeting.
 *
 * It's goal is to house all logic required for interacting with a Question in the context of a Meeting.
 *
 */
export class MeetingQuestion extends Question {
  meeting: MeetingCubit
  constructor(meeting: MeetingCubit, model: SlideQuestion) {
    super(model)
    this.meeting = meeting
    makeObservable(this, {
      userAnswer: computed,
      groupAnswer: computed,
      allAnswers: computed,
      allSubmittedAnswers: computed,
      isAnswered: computed,
      isSubmitted: computed,
      collaborationData: computed,
      collaborationDataForCurrentUser: computed,
      collaborationDataForResult: computed,
      figJamCollaborationDataForCurrentUser: computed,
    })
  }

  get collaborationData() {
    return this.meeting.collaborationData.models.filter(
      (data) => data.data.questionId === this.id
    )
  }

  get collaborationDataForCurrentUser() {
    return this.collaborationData.filter(
      (data) => data.data.userId === this.meeting.currentUser.uid
    )
  }

  getLowestOrderForCollaborationData() {
    const orders = this.collaborationDataForCurrentUser.map(
      (data) => data.data.location?.order || 0
    )
    const lowestOrder =
      orders.length === 0
        ? 0
        : this.collaborationDataForCurrentUser.reduce(
            (min, data) => Math.min(min, data.data.location?.order || 0),
            Number.MAX_SAFE_INTEGER
          )
    return lowestOrder
  }

  get figJamCollaborationDataForCurrentUser() {
    const figJamData = this.collaborationDataForCurrentUser.filter(
      (data) => data.data.collaborationDataType === CollaborationDataType.figJam
    )
    const sortedEntries = figJamData.sort((a, b) => {
      const orderA = a.data.location?.order
      const orderB = b.data.location?.order
      if (orderA === undefined || orderB === undefined) {
        return 0
      }
      return orderA > orderB ? 1 : orderA < orderB ? -1 : 0
    })
    return sortedEntries
  }

  get slideQuestion() {
    return this.model
  }

  get userAnswer() {
    const user = this.meeting.currentUser
    const collection = this.meeting.roomStateAnswersPerUser.get(user.uid)
    if (!collection) return undefined
    return collection.models.find((ua) => ua.data.slideQuestionId === this.id)
  }

  get groupAnswer() {
    return this.meeting.roomStateAnswersForGroup.models.find(
      (ua) => ua.data.slideQuestionId === this.id
    )
  }

  get isAnswered() {
    if (this.meeting.currentSlide instanceof GroupQuizSlide) {
      return !!this.groupAnswer
    }
    return !!this.userAnswer
  }

  get isSubmitted() {
    if (this.meeting.currentSlide instanceof GroupQuizSlide) {
      return !!this.groupAnswer?.data.submitted
    }
    return !!this.userAnswer?.data.submitted
  }

  get allAnswers() {
    return this.meeting.questionAnswers.get(this.id) || []
  }

  get allSubmittedAnswers() {
    return this.allAnswers.filter((a) => a.data.submitted)
  }

  answer(answer: number, answerList?: number[]) {
    return this.sendUserAnswer({
      answer,
      answerList,
      isGroupAnswer: false,
    })
  }

  answerForGroup(answer: number, answerList?: number[]) {
    return this.sendUserAnswer({
      answer,
      answerList,
      isGroupAnswer: true,
    })
  }

  protected sendUserAnswer({
    answer,
    answerList,
    isGroupAnswer,
  }: {
    isGroupAnswer: boolean
    answer: number
    answerList?: number[]
  }) {
    const meeting = this.meeting

    if (!meeting.roomId) return Promise.resolve()

    const answerId = isGroupAnswer ? this.groupAnswer?.id : this.userAnswer?.id

    meeting.logEvent('meeting_answer_question', {
      questionId: this.id,
      answer,
      isGroupAnswer,
    })

    return upsertUserAnswer(meeting.repository, this.id, {
      userId: meeting.currentUser.uid,
      roomId: meeting.roomId,
      assignmentId: meeting.assignmentId || undefined,
      sectionId: meeting.sectionId || undefined,
      answerId: answerId,
      answer,
      answerList,
      isGroupAnswer,
    })
  }

  async upsertAnswerObject({
    isGroupAnswer,
    objects,
    submitted,
  }: {
    submitted?: boolean
    isGroupAnswer: boolean
    objects: {
      id: string
      text: string
      userId?: string
    }[]
  }) {
    const answer = isGroupAnswer ? this.groupAnswer : this.userAnswer
    const answerObjects = objects.reduce(
      (acc, obj, index) => ({
        ...acc,
        [obj.id]: {
          id: obj.id,
          text: obj.text,
          userId: obj.userId,
          order: index,
        },
      }),
      {}
    )

    const meeting = this.meeting
    if (!meeting.roomId) return Promise.resolve()

    await upsertUserAnswer(meeting.repository, this.id, {
      userId: meeting.currentUser.uid,
      roomId: meeting.roomId,
      answerId: answer?.id,
      answer: 0,
      isGroupAnswer: isGroupAnswer,
      answerObjects: answerObjects,
      submitted: submitted,
    })
  }

  async submitUserAnswerObject(
    objects: {
      id: string
      text: string
    }[]
  ) {
    return this.upsertAnswerObject({
      isGroupAnswer: false,
      submitted: true,
      objects,
    })
  }

  async submitGroupAnswerObject(
    objects: {
      id: string
      text: string
    }[]
  ) {
    return this.upsertAnswerObject({
      isGroupAnswer: true,
      submitted: true,
      objects,
    })
  }

  reorderUserAnswerObject(
    list: {
      id: string
      order: number
    }[]
  ) {
    const meeting = this.meeting
    if (!meeting.roomId) return Promise.resolve()

    return updateAnswerObjectsOrder(meeting.repository, {
      userId: meeting.currentUser.uid,
      roomId: meeting.roomId,
      answerId: this.id,
      isGroupAnswer: false,
      answerObjects: list,
    })
  }

  removeUserAnswerObject(id: string) {
    const answerObjects = this.userAnswer?.data.answerObjects || {}

    if (!answerObjects[id]) return Promise.resolve()

    const cloned = { ...answerObjects }

    delete cloned[id]

    const meeting = this.meeting
    if (!meeting.roomId) return Promise.resolve()

    return setAnswerObjects(meeting.repository, {
      userId: meeting.currentUser.uid,
      roomId: meeting.roomId,
      answerId: this.id,
      isGroupAnswer: false,
      answerObjects: cloned,
    })
  }

  updateGroupAnswerObject({
    id,
    text,
    order,
  }: {
    id: string
    text: string
    order: number
  }) {
    const meeting = this.meeting
    if (!meeting.roomId) return Promise.resolve()

    return updateAnswerObjects(meeting.repository, {
      userId: meeting.currentUser.uid,
      roomId: meeting.roomId,
      answerId: this.id,
      isGroupAnswer: true,
      answerObjects: [
        {
          id,
          text,
          order,
        },
      ],
    })
  }

  reorderGroupAnswerObject(
    list: {
      id: string
      order: number
      text: string
      userId: string
    }[]
  ) {
    const meeting = this.meeting
    if (!meeting.roomId) return Promise.resolve()

    return updateAnswerObjectsOrder(meeting.repository, {
      userId: meeting.currentUser.uid,
      roomId: meeting.roomId,
      answerId: this.id,
      isGroupAnswer: true,
      answerObjects: list.map((item) => ({
        id: item.id,
        order: item.order,
        text: item.text,
        userId: item.userId,
      })),
    })
  }

  removeUserAnswer(answerId: string) {
    const meeting = this.meeting
    if (!meeting.roomId) return
    return removeUserAnswer(meeting.firestore, {
      roomId: meeting.roomId,
      userId: meeting.currentUser.uid,
      answerId,
      isGroupAnswer: !!this.groupAnswer,
    })
  }

  submitAnswer() {
    this.meeting.logEvent('meeting_submit_question_answer', {
      questionId: this.id,
    })
    this.submitUserAnswer({ isGroupAnswer: false })
  }

  submitGroupAnswer() {
    this.meeting.logEvent('meeting_submit_question_group_answer', {
      questionId: this.id,
    })
    this.submitUserAnswer({ isGroupAnswer: true })
  }

  protected async submitUserAnswer({
    isGroupAnswer,
  }: {
    isGroupAnswer: boolean
  }) {
    const meeting = this.meeting
    if (!meeting.roomId) return

    const answerId = isGroupAnswer ? this.groupAnswer?.id : this.userAnswer?.id

    if (!answerId) return

    submitUserAnswer(meeting.firestore, {
      roomId: meeting.roomId,
      userId: meeting.currentUser.uid,
      answerId,
      isGroupAnswer,
    })
  }

  upsertCollaborationData(
    id: string,
    data: {
      type: CollaborationDataType
      label: string
      location: {
        x: number
        y: number
        container: string
        order?: number
      }
      sourceId?: string
      userId: string
    }
  ) {
    return upsertRoomStateCollaborationData(this.meeting.repository, {
      id,
      roomId: this.meeting.roomId,
      data: {
        ...data,
        questionId: this.id,
        collaborationDataType: data.type,
      },
    })
  }

  removeCollaborationData(id: string) {
    return deleteRoomStateCollaborationData(this.meeting.repository, {
      id,
      roomId: this.meeting.roomId,
    })
  }

  reorderCollaborationData(
    list: {
      id: string
      order: number
      container: string
      x?: number
      y?: number
    }[]
  ) {
    const meeting = this.meeting
    if (!meeting.roomId) return Promise.resolve()

    return updateRoomStateCollaborationDataOrder(meeting.repository, {
      roomId: meeting.roomId,
      list: list,
    })
  }

  saveCollaborationDataAsUserAnswer({
    submitted = true,
  }: {
    submitted: boolean
  }) {
    const answerObject = this.figJamCollaborationDataForCurrentUser.map(
      (data) => ({
        id: data.id,
        text: data.data.label || '',
      })
    )

    return this.upsertAnswerObject({
      isGroupAnswer: false,
      submitted: submitted,
      objects: answerObject,
    })
  }

  get collaborationDataForResult() {
    return this.collaborationData.filter(
      (data) => data.data.location?.container === 'result'
    )
  }

  saveResultAsGroupAnswer({ submitted = true }: { submitted: boolean }) {
    const collabData = this.collaborationData
    const resultDatas = collabData.filter(
      (data) => data.data.location?.container === 'result'
    )

    const result = resultDatas.map((data) => ({
      id: data.id,
      text: data.data.label || '',
      userId: data.data.userId || '',
    }))

    return this.upsertAnswerObject({
      isGroupAnswer: true,
      submitted: submitted,
      objects: result,
    })
  }

  resetCollaborationData() {
    const promises: Promise<void>[] = []
    for (const data of this.meeting.collaborationData.models) {
      if (data.data.questionId === this.id) {
        const promise = deleteRoomStateCollaborationData(
          this.meeting.repository,
          {
            id: data.id,
            roomId: this.meeting.roomId,
          }
        )
        promises.push(promise)
      }
    }
    return Promise.all(promises)
  }
}
